import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fromWithCompany, supabase } from "../config/supabaseClient";
import { startLoading, stopLoading } from "./commonSlice";
import { fetchEmployees } from "./employeeSlice";
import { messages } from "../constant/alertMessages";
import { toast } from "react-toastify";

// ✅ Fetch Company Config
export const fetchConfig = createAsyncThunk(
  "config/fetchConfig",
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data, error } = await supabase
        .from("configurations")
        .select("*")
        .eq("company_id", companyId)
        .maybeSingle();
      if (error) throw error;
      return data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }finally{
      dispatch(stopLoading());
    }
  }
);

// ✅ Add Company Config
// export const addConfig = createAsyncThunk(
//   "config/addConfig",
//   async (data, { rejectWithValue, dispatch }) => {
//     try {
//       dispatch(startLoading());
//       const { data: config, error } = await supabase
//         .from("configurations")
//         .insert(data)
//         .select("*")
//         .single();
//       if (error) throw error;
//       dispatch(stopLoading());
//       return config;
//     } catch (error) {
//       return rejectWithValue(error.message);
//     }
//   }
// );

// ✅ Update Company Config
export const updateConfig = createAsyncThunk(
  "config/updateConfig",
  async ({ companyId, data }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      dispatch(fetchEmployees(companyId)).then((res) => {
        if (!res.payload.error && res.payload.length > 0) {
          toast.error(messages.REMOVE_EXISTING_EMPLOYEE);
          return;
        }
      });

      const { data: updatedData, error } = await supabase
        .from("configurations")
        .update(data)
        .select("*")
        .eq("company_id", companyId)
        .single();
      if (error) throw error;
      dispatch(stopLoading());
      return updatedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Fetch configuration
export const fetchConfiguration = createAsyncThunk(
  "config/fetchConfiguration",
  async ({ yearId, companyId }) => {
    // Prevent API call if companyId is not provided
    if (!companyId) {
      throw new Error("Company ID is required");
    }
    
    const { data, error } = await fromWithCompany("configurations")
      .select("*")
      .eq("year_id", yearId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    // If no configuration exists, create a default one
    if (!data) {
      const { data: newConfig, error: createError } = await supabase
        .from("configurations")
        .insert([
          {
            year_id: yearId,
            company_id: companyId,
            training_need_workflow: false,
            select_training: false,
            add_adhoc_training: false,
            training_need_collection_start_date: null,
            employee_submission_date: null,
            manager_review_submission_date: null,
            reminder_interval: "-1",
          },
        ])
        .select()
        .single();

      if (createError) {
        console.error("Error creating new configuration:", createError);
        throw createError;
      }
      return newConfig;
    }

    return data;
  }
);
// ✅ Update configuration
export const updateConfiguration = createAsyncThunk(
  "config/updateConfiguration",
  async ({ yearId, companyId, updateData }) => {
    // First check if configuration exists
    const { data: existingConfig, error: checkError } = await supabase
      .from("configurations")
      .select("*")
      .eq("year_id", yearId)
      .eq("company_id", companyId)
      .maybeSingle();

    if (checkError && checkError.code !== "PGRST116") {
      throw checkError;
    }

    let result;
    // If configuration doesn't exist, create it with updateData
    if (!existingConfig) {
      const { data: newConfig, error: createError } = await supabase
        .from("configurations")
        .insert([
          {
            year_id: yearId,
            company_id: companyId,
            training_need_workflow: false,
            select_training: false,
            add_adhoc_training: false,
            training_need_collection_start_date: null,
            employee_submission_date: null,
            manager_review_submission_date: null,
            reminder_interval: "-1",
            ...updateData,
          },
        ])
        .select()
        .single();

      if (createError) {
        throw createError;
      }
      result = newConfig;
    } else {
      // Update existing configuration
      const { data: updatedData, error: updateError } = await supabase
        .from("configurations")
        .update(updateData)
        .eq("year_id", yearId)
        .eq("company_id", companyId)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }
      result = updatedData;
    }

    return result;
  }
);

// ✅ Handle Training Need Collection Toggle
export const handleTrainingNeedCollectionToggle = createAsyncThunk(
  "config/handleTrainingNeedCollectionToggle",
  async ({ yearId, companyId, isEnabled }, { rejectWithValue }) => {
    try {
      const currentDate = new Date().toISOString();
      
      // First check if configuration exists
      const { data: existingConfig, error: checkError } = await supabase
        .from("configurations")
        .select("*")
        .eq("year_id", yearId)
        .eq("company_id", companyId)
        .maybeSingle();

      if (checkError && checkError.code !== "PGRST116") {
        throw checkError;
      }

      let result;
      // If configuration doesn't exist, create it
      if (!existingConfig) {
        const { data: newConfig, error: createError } = await supabase
          .from("configurations")
          .insert([
            {
              year_id: yearId,
              company_id: companyId,
              training_need_workflow: false,
              select_training: false,
              add_adhoc_training: false,
              training_need_collection_start_date: isEnabled ? currentDate : null,
              employee_submission_date: null,
              manager_review_submission_date: null,
              reminder_interval: "-1",
              updated_at: currentDate,
            },
          ])
          .select()
          .single();

        if (createError) {
          throw createError;
        }
        result = newConfig;
      } else {
        // Update existing configuration
        const { data: updatedData, error: updateError } = await supabase
          .from("configurations")
          .update({
            training_need_collection_start_date: isEnabled ? currentDate : null,
            updated_at: currentDate,
          })
          .eq("year_id", yearId)
          .eq("company_id", companyId)
          .select()
          .single();

        if (updateError) {
          throw updateError;
        }
        result = updatedData;
      }

      return result;
    } catch (error) {
      console.error("Error in updateTrainingNeedCollection:", error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  configData: null,
  loading: false,
  error: null,
  isWorkflowReq: null,
};

const configSlice = createSlice({
  name: "config",

  initialState: {
    configData: null,
    isWorkflowReq: null,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Configuration
      .addCase(fetchConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        state.configData = action.payload;
        state.isWorkflowReq = action.payload.training_need_workflow;
      })
      .addCase(fetchConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update Configuration
      .addCase(updateConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        state.configData = action.payload;
        state.isWorkflowReq = action.payload.training_need_workflow;
      })
      .addCase(updateConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update Training Need Collection
      .addCase(handleTrainingNeedCollectionToggle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        handleTrainingNeedCollectionToggle.fulfilled,
        (state, action) => {
          state.loading = false;
          state.configData = action.payload;
        }
      )
      .addCase(handleTrainingNeedCollectionToggle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Original cases
      // .addCase(addConfig.pending, (state) => {
      //   state.error = null;
      // })
      // .addCase(addConfig.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.isWorkflowReq = action.payload?.training_need_workflow;
      // })
      // .addCase(addConfig.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload;
      // })
      .addCase(fetchConfig.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.isWorkflowReq = action.payload.training_need_workflow;
        state.configData = action.payload;
      })
      .addCase(fetchConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateConfig.pending, (state) => {
        state.error = null;
      })
      .addCase(updateConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.isWorkflowReq = action.payload.training_need_workflow;
      })
      .addCase(updateConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setLoading, clearError } = configSlice.actions;
export default configSlice.reducer;
